import { ref, computed, toValue, watch } from 'vue';
import { get } from 'lodash';
import { useQueryClient } from '@tanstack/vue-query';

import {
  str as StrLib,
  SpotType,
  SpotStatus,
  getSpotStatus,
  isSpotAssignedToAssetVisit,
  isSpotAssignedToAssetContainer,
  AssetContainerType
} from '@nova/core';
import { taskKeys } from '@/composables/queryKeys';
import { useSpotAreasByWarehouseId, useSpotsByWarehouseId } from '@/composables';
import yardTasksService from '@satellite/services/yard-tasks-service';

import type { ISpot, INovaEntity } from '@nova/core';
import type { MaybeRefOrGetter, Ref, ComputedRef } from 'vue';

interface ISpotWithAreaId extends ISpot {
  spotAreaId?: string;
}

interface ISpotArea extends INovaEntity {
  name: string;
  warehouseId: string;
  orgId: string;
}

interface IGroupedSpot {
  code: string;
  id: string;
  icon: string;
  group: string;
  status: string;
  disabled: boolean;
  raw: ISpotWithAreaId;
}

type IGroupedItem = { header: string } | IGroupedSpot;

interface IMoveTaskChild {
  originSpot?: { id: string };
  destinationSpot?: { id: string };
}

interface ITask {
  child: IMoveTaskChild;
}

interface ITaskListResponse {
  items: ITask[];
}

type ActiveMoveTasksBySpotId = Record<string, ITask[]>;

/**
 * Options for useYardSpotSelect composable.
 *
 * Note on warehouseId type: Accepts MaybeRefOrGetter<string | null> OR { value: string | null } (ref-like object).
 * The latter is needed because Pinia stores return refs with structural typing
 * ({ value: T; [RefSymbol]: true }) rather than the nominal Ref<T> type that
 * MaybeRefOrGetter expects. This is a TypeScript limitation where Pinia's type
 * extraction doesn't preserve the exact Ref<T> type annotation.
 * Since toValue() handles both at runtime, this union type allows direct
 * usage of Pinia store refs without wrapping them in computed().
 */
interface IUseYardSpotSelectOptions {
  warehouseId?: MaybeRefOrGetter<string | null> | { value: string | null };
  params?: Record<string, unknown>;
  includeClosedSpots?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssignedSpots?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeReservedSpots?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssociatedToActiveMoveTasks?: MaybeRefOrGetter<boolean | 'disabled'>;
  assetType?: MaybeRefOrGetter<AssetContainerType | null>;
  search?: MaybeRefOrGetter<string>;
  showDisabled?: MaybeRefOrGetter<boolean>;
}

interface IYardSpotSelect {
  spotsRef: Ref<ISpotWithAreaId[]>;
  spotAreasRef: Ref<ISpotArea[]>;
  spotsByAreaRef: ComputedRef<IGroupedItem[]>;
  isFetchingSpotsRef: Ref<boolean>;
  isFetchingAreasRef: Ref<boolean>;
  isFetchingRef: ComputedRef<boolean>;
  hasNoSpotsRef: ComputedRef<boolean>;
  fetchSpots: (targetWarehouseId?: string | null) => Promise<ISpotWithAreaId[]>;
  fetchSpotAreas: (targetWarehouseId?: string | null) => Promise<ISpotArea[]>;
  fetchActiveMoveTasks: (spotIds: string[]) => Promise<ActiveMoveTasksBySpotId>;
  fetchAll: (targetWarehouseId?: string | null) => Promise<void>;
  isSpotOpen: (spot: ISpot) => boolean;
  isSpotClosed: (spot: ISpot) => boolean;
  isSpotAssigned: (spot: ISpot) => boolean;
  isSpotReserved: (spot: ISpot) => boolean;
  isSpotInActiveMoveTask: (spotId: string) => boolean;
  hasAssetVisitOnSpot: (spot: ISpot) => boolean;
  hasAssetContainerOnSpot: (spot: ISpot) => boolean;
  getSpotIcon: (type: string) => string;
  getSpotStatusKey: (spot: ISpot) => string;
}

interface AreaData {
  area: ISpotArea;
  spots: IGroupedSpot[];
}

/**
 * Composable for spot selection functionality.
 * Uses TanStack Query composables for data fetching (circe-specific).
 */
export function useYardSpotSelect(options: IUseYardSpotSelectOptions = {}): IYardSpotSelect {
  const {
    warehouseId,
    params = {},
    includeClosedSpots = false,
    includeAssignedSpots = false,
    includeReservedSpots = false,
    includeAssociatedToActiveMoveTasks = false,
    assetType = null,
    search = '',
    showDisabled = true
  } = options;

  const queryClient = useQueryClient();

  const getWarehouseId = () => {
    // Use toValue() to handle both MaybeRefOrGetter and { value: T } ref-like objects
    const id = toValue(warehouseId as MaybeRefOrGetter<string | null>);
    return id || null;
  };

  const activeMoveTasksBySpotId = ref<ActiveMoveTasksBySpotId>({});

  const spotsQuery = useSpotsByWarehouseId<ISpotWithAreaId[]>(warehouseId, () => params, {
    enabled: () => Boolean(getWarehouseId()),
    joins: [
      'spotAssignments',
      'spotAssignments.assetVisit',
      'spotAssignments.assetVisit.assetContainers',
      'spotAssignments.assetContainer'
    ]
  });

  const spotAreasQuery = useSpotAreasByWarehouseId<ISpotArea[]>(
    warehouseId as MaybeRefOrGetter<string | null | undefined>,
    {},
    {
      enabled: computed(() => Boolean(getWarehouseId()))
    }
  );

  const spots = computed(() => (spotsQuery.data.value as ISpotWithAreaId[] | undefined) || []);
  const spotAreas = computed(() => (spotAreasQuery.data.value as ISpotArea[] | undefined) || []);
  const isFetchingSpots = computed(() => spotsQuery.isFetching.value);
  const isFetchingAreas = computed(() => spotAreasQuery.isFetching.value);

  /**
   * Get spot icon based on spot type.
   */
  const getSpotIcon = (type: string): string => {
    return type === SpotType.DOCKING ? 'mdi-truck-outline' : 'mdi-parking';
  };

  /**
   * Check if a spot is open.
   */
  const isSpotOpen = (spot: ISpot): boolean => getSpotStatus(spot) === SpotStatus.OPEN;

  /**
   * Check if a spot is closed.
   */
  const isSpotClosed = (spot: ISpot): boolean => getSpotStatus(spot) === SpotStatus.CLOSED;

  /**
   * Check if a spot is assigned.
   */
  const isSpotAssigned = (spot: ISpot): boolean => getSpotStatus(spot) === SpotStatus.ASSIGNED;

  /**
   * Check if a spot is reserved.
   */
  const isSpotReserved = (spot: ISpot): boolean => getSpotStatus(spot) === SpotStatus.RESERVED;

  /**
   * Check if a spot is part of an active move task.
   */
  const isSpotInActiveMoveTask = (spotId: string): boolean => {
    if (!spotId) {
      return false;
    }
    return Boolean(activeMoveTasksBySpotId.value[spotId]?.length);
  };

  /**
   * Check if a spot has an asset visit assigned.
   */
  const hasAssetVisitOnSpot = (spot: ISpot): boolean => isSpotAssignedToAssetVisit(spot);

  /**
   * Check if a spot has an asset container assigned.
   */
  const hasAssetContainerOnSpot = (spot: ISpot): boolean => isSpotAssignedToAssetContainer(spot);

  /**
   * Fetch spot areas for the warehouse.
   */
  const fetchSpotAreas = async (targetWarehouseId: string | null = null): Promise<ISpotArea[]> => {
    const id = targetWarehouseId || getWarehouseId();
    if (!id) {
      console.warn('[useYardSpotSelect] No warehouseId provided to fetchSpotAreas');
      return [];
    }

    try {
      await spotAreasQuery.refetch();
      return spotAreas.value;
    } catch (error) {
      console.error('[useYardSpotSelect] Failed to fetch spot areas:', error);
      return [];
    }
  };

  /**
   * Fetch spots for the warehouse.
   */
  const fetchSpots = async (
    targetWarehouseId: string | null = null
  ): Promise<ISpotWithAreaId[]> => {
    const id = targetWarehouseId || getWarehouseId();
    if (!id) {
      console.warn('[useYardSpotSelect] No warehouseId provided to fetchSpots');
      return [];
    }

    try {
      await spotsQuery.refetch();
      return spots.value;
    } catch (error) {
      console.error('[useYardSpotSelect] Failed to fetch spots:', error);
      return [];
    }
  };

  /**
   * Fetch active move tasks for the given spot IDs.
   */
  const fetchActiveMoveTasks = async (spotIds: string[]): Promise<ActiveMoveTasksBySpotId> => {
    if (!Array.isArray(spotIds) || spotIds.length === 0) {
      activeMoveTasksBySpotId.value = {};
      return {};
    }

    try {
      const params = {
        statuses: 'created,in-progress',
        type: 'move'
      };

      const response = await queryClient.fetchQuery<ITaskListResponse>({
        queryKey: taskKeys.activeMoveTasks(params),
        queryFn: ({ signal }) =>
          yardTasksService.getYardTaskList({
            requestConfig: {
              params,
              signal
            }
          }) as Promise<ITaskListResponse>
      });

      const tasks = get(response, 'items') || ([] as ITask[]);

      const tasksBySpotId: ActiveMoveTasksBySpotId = {};
      const spotIdsSet = new Set(spotIds);
      tasks.forEach((task: ITask) => {
        const originSpotId = get(task, 'child.originSpot.id') as string | undefined;
        const destinationSpotId = get(task, 'child.destinationSpot.id') as string | undefined;

        if (originSpotId && spotIdsSet.has(originSpotId)) {
          tasksBySpotId[originSpotId] = [...(tasksBySpotId[originSpotId] || []), task];
        }
        if (destinationSpotId && spotIdsSet.has(destinationSpotId)) {
          tasksBySpotId[destinationSpotId] = [...(tasksBySpotId[destinationSpotId] || []), task];
        }
      });

      activeMoveTasksBySpotId.value = tasksBySpotId;
      return tasksBySpotId;
    } catch (error) {
      console.warn('[useYardSpotSelect] Failed to fetch active move tasks:', error);
      activeMoveTasksBySpotId.value = {};
      return {};
    }
  };

  /**
   * Fetch all required data (spots, spot areas, active move tasks).
   * This is a convenience method to fetch everything at once.
   */
  const fetchAll = async (targetWarehouseId: string | null = null): Promise<void> => {
    const id = targetWarehouseId || getWarehouseId();
    if (!id) {
      console.warn('[useYardSpotSelect] No warehouseId provided to fetchAll');
      return Promise.resolve();
    }

    await Promise.all([spotAreasQuery.refetch(), spotsQuery.refetch()]);

    const spotIds = getActiveSpotIds();
    await fetchActiveMoveTasks(spotIds);
  };

  watch(
    () => spots.value,
    newSpots => {
      if (newSpots.length > 0) {
        const spotIds = getActiveSpotIds();
        void fetchActiveMoveTasks(spotIds);
      }
    },
    { immediate: true }
  );

  /**
   * Check if asset should be excluded based on filters
   */
  const shouldExcludeClosedSpot = (spot: ISpot): boolean => {
    return isSpotClosed(spot);
  };

  const shouldExcludeReservedSpot = (spot: ISpot): boolean => {
    return isSpotReserved(spot);
  };

  const shouldExcludeAssignedSpot = (spot: ISpot): boolean => {
    return isSpotAssigned(spot);
  };

  const shouldExcludeSpotAssociatedToActiveMoveTask = (spot: ISpotWithAreaId): boolean => {
    return isSpotInActiveMoveTask(spot.id);
  };

  /**
   * Check if spot should be disabled based on asset type requirements
   */
  const shouldDisableByAssetType = (spot: ISpot): boolean => {
    const assetTypeValue = toValue(assetType);

    if (!assetTypeValue || isSpotOpen(spot)) {
      return false;
    }

    if (assetTypeValue === AssetContainerType.Trailer) {
      // For assetVisit: spot must have assetContainer, but not assetVisit
      return !hasAssetContainerOnSpot(spot) || hasAssetVisitOnSpot(spot);
    }

    if (assetTypeValue === AssetContainerType.Container) {
      // For assetContainer: spot must not have assetContainer
      return hasAssetContainerOnSpot(spot);
    }

    return false;
  };

  /**
   * Check if spot should be disabled (included but not selectable)
   */
  const shouldDisableSpot = (spot: ISpotWithAreaId): boolean => {
    const includeClosedValue = toValue(includeClosedSpots);
    const includeAssignedValue = toValue(includeAssignedSpots);
    const includeReservedValue = toValue(includeReservedSpots);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const includeClosedDisabled = includeClosedValue === 'disabled' && isSpotClosed(spot);
    const includeAssignedDisabled = includeAssignedValue === 'disabled' && isSpotAssigned(spot);
    const includeReservedDisabled = includeReservedValue === 'disabled' && isSpotReserved(spot);
    const includeMoveTaskDisabled =
      includeMoveTaskValue === 'disabled' && isSpotInActiveMoveTask(spot.id);

    return (
      includeClosedDisabled ||
      includeAssignedDisabled ||
      includeReservedDisabled ||
      includeMoveTaskDisabled ||
      shouldDisableByAssetType(spot)
    );
  };

  /**
   * Get translated spot status.
   * Note: This requires i18n context. For now, we'll return the status key.
   * Apps should provide their own translation function if needed.
   */
  const getSpotStatusKey = (spot: ISpot): string => {
    const status = getSpotStatus(spot);

    if (status === SpotStatus.OPEN) {
      return 'open';
    }

    if (status === SpotStatus.CLOSED) {
      return 'closed';
    }

    if (status === SpotStatus.ASSIGNED) {
      return 'assigned';
    }

    if (status === SpotStatus.RESERVED) {
      return 'reserved';
    }

    return 'unknown';
  };

  const groupedSpotsByArea = computed(() => {
    if (isFetchingSpots.value || isFetchingAreas.value) {
      return [];
    }

    const rawSpots = spots.value;
    const rawAreas = spotAreas.value;
    const searchQuery = toValue(search)?.trim().toLowerCase() || '';
    const showDisabledValue = toValue(showDisabled);

    const includeClosedValue = toValue(includeClosedSpots);
    const includeAssignedValue = toValue(includeAssignedSpots);
    const includeReservedValue = toValue(includeReservedSpots);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const exclusions = [
      ...(includeClosedValue ? [] : [shouldExcludeClosedSpot]),
      ...(includeReservedValue ? [] : [shouldExcludeReservedSpot]),
      ...(includeAssignedValue ? [] : [shouldExcludeAssignedSpot]),
      ...(includeMoveTaskValue ? [] : [shouldExcludeSpotAssociatedToActiveMoveTask])
    ];

    const isExcludedBySomeFilter = (spot: ISpotWithAreaId): boolean =>
      exclusions.some(exclude => exclude(spot));

    /**
     * Helper function to check if a string value matches the search query.
     * Handles null/undefined values and performs case-insensitive matching.
     */
    const matchesSearchString = (value: string | null | undefined): boolean => {
      if (!value || !searchQuery) {
        return false;
      }
      return value.toLowerCase().includes(searchQuery);
    };

    const areasMap = new Map<string, AreaData>();
    rawAreas.forEach((area: ISpotArea) => {
      areasMap.set(area.id, {
        area,
        spots: []
      });
    });

    const matchesSearch = (spot: ISpotWithAreaId): boolean => {
      if (!searchQuery) {
        return true;
      }
      const spotCodeMatch = matchesSearchString(spot.code);
      const areaData = areasMap.get(spot.spotAreaId || '');
      const areaNameMatch = matchesSearchString(areaData?.area.name);
      return spotCodeMatch || areaNameMatch;
    };

    // Filter and group spots by area
    rawSpots
      .filter(spot => {
        if (!spot.spotAreaId) {
          return false;
        }

        if (isExcludedBySomeFilter(spot) || !matchesSearch(spot)) {
          return false;
        }

        if (!showDisabledValue && shouldDisableSpot(spot)) {
          return false;
        }

        return true;
      })
      .forEach(spot => {
        const areaData = areasMap.get(spot.spotAreaId!);
        if (!areaData) {
          return;
        }

        const isSpotDisabled = shouldDisableSpot(spot);

        areaData.spots.push({
          code: spot.code,
          id: spot.id,
          icon: getSpotIcon(spot.type),
          group: areaData.area.name,
          status: getSpotStatusKey(spot),
          disabled: isSpotDisabled,
          raw: spot
        });
      });

    // Build final items array with headers
    const items: IGroupedItem[] = [];
    areasMap.forEach(areaData => {
      if (areaData.spots.length > 0) {
        // Sort spots within the area
        areaData.spots.sort((a: IGroupedSpot, b: IGroupedSpot) =>
          StrLib.compareAlphaNumeric(a.code, b.code)
        );

        items.push({ header: areaData.area.name.toUpperCase() });
        items.push(...areaData.spots);
      }
    });

    return items;
  });

  const getActiveSpotIds = () => {
    return groupedSpotsByArea.value
      .map((item: IGroupedItem) => {
        if ('header' in item) {
          return;
        }
        return item.id;
      })
      .filter(Boolean) as string[];
  };

  const hasNoSpots = computed(() => {
    if (isFetchingSpots.value || isFetchingAreas.value) {
      return false;
    }

    return groupedSpotsByArea.value.length === 0;
  });

  return {
    spotsRef: computed(() => spots.value) as Ref<ISpotWithAreaId[]>,
    spotAreasRef: computed(() => spotAreas.value) as Ref<ISpotArea[]>,
    spotsByAreaRef: groupedSpotsByArea,
    isFetchingSpotsRef: computed(() => isFetchingSpots.value),
    isFetchingAreasRef: computed(() => isFetchingAreas.value),
    isFetchingRef: computed(() => isFetchingSpots.value || isFetchingAreas.value),
    hasNoSpotsRef: hasNoSpots,

    fetchSpots,
    fetchSpotAreas,
    fetchActiveMoveTasks,
    fetchAll,

    isSpotOpen,
    isSpotClosed,
    isSpotAssigned,
    isSpotReserved,
    isSpotInActiveMoveTask,
    hasAssetVisitOnSpot,
    hasAssetContainerOnSpot,
    getSpotIcon,
    getSpotStatusKey
  };
}
